'use client';

import { timezones } from '@wolfejs/core/utils/timezones';
import type { UserUpdatePayload } from '@wolfejs/ms-api-js/src/types/users';
import { useAdminUserUpdate2 } from '@wolfejs/ms-api-react/src/hooks';
import { Form } from '@wolfejs/ui/Form/Form';
import type { FormSchemaEntry } from '@wolfejs/ui/Form/types';
import type { SelectOption } from '@wolfejs/ui/Select';
import { UI } from '@wolfejs/ui/UI';
import type { UIComponentProps } from '@wolfejs/ui/types/UI';
import { UIColorContext } from '@wolfejs/ui/types/UI';
import { cn } from '@wolfejs/ui/utils/classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { z } from 'zod';

const schema: FormSchemaEntry[] = [
  {
    label: 'Username',
    name: 'username',
    type: 'text',
    validation: z.string().min(2),
    showRequiredIndicator: true,
  },
  {
    label: 'Full Name',
    name: 'fullName',
    type: 'text',
    validation: z.string().min(2),
    showRequiredIndicator: true,
  },
  {
    label: 'Your email',
    name: 'email',
    type: 'email',
    validation: z.string().min(2, { message: 'This field has to be filled.' }).email('This is not a valid email.'),
    showRequiredIndicator: true,
  },
  {
    label: 'Phone Number',
    name: 'phone',
    type: 'phone',
    validation: z.string().min(10, 'Invalid phone number'),
    showRequiredIndicator: true,
  },
  {
    label: 'Country',
    name: 'country',
    type: 'text',
    validation: z.string().optional(),
  },
  {
    label: 'City',
    name: 'city',
    type: 'text',
    validation: z.string().optional(),
  },
  {
    label: 'Timezone',
    name: 'timezone',
    type: 'select',
    props: {
      fullWidth: true,
      color: UIColorContext.primary,
      options: timezones,
    },
    validation: z
      .object({
        value: z.string().optional(),
        label: z.string().optional(),
      })
      .optional(),
  },
  // TODO: removed for now, as not yet clear what types of account there will be
  // {
  //   label: 'Account type',
  //   name: 'accountType',
  //   type: 'select',
  //   props: {
  //     color: UIColorContext.primary,
  //     fullWidth: true,
  //     options: [
  //       { value: 'personal', label: 'Personal' },
  //       { value: 'business', label: 'Business' },
  //     ],
  //   },
  //   validation: z.string().optional(),
  // },
];

export type AccountFormValues = {
  username: string;
  fullName: string;
  email: string;
  phone: string;
  country?: string;
  city?: string;
  timezone?: SelectOption;
};

export type AdminAccountProps = UIComponentProps & {
  defaultValues?: Partial<AccountFormValues>;
  append?: ReactNode;
  userId?: string;
  onSubmit?: (values: Partial<AccountFormValues>) => void;
  onChange?: (values: Partial<AccountFormValues>) => void;
};
export const AdminAccountForm = ({ className, defaultValues, append, userId, ...otherProps }: AdminAccountProps) => {
  const [submitttedValues, setSubmitttedValues] = useState<Partial<AccountFormValues>>();
  const updateUserAccount = useAdminUserUpdate2();

  const onSubmit = async (data: Partial<AccountFormValues>) => {
    const updateUserPayload: UserUpdatePayload = {
      id: userId as string,
      name: data.username || '',
      // email: data.email || '', // NOTE: user should not be allowed to update email like this

      profile: {
        timezone: +(data.timezone?.value as string)?.replace('UTC', ''),
        firstname: data.fullName?.split(' ')[0],
        lastname: data.fullName?.split(' ')[1],
        phoneNumber: data.phone,
        address: {
          country: data.country || '',
          city: data.city || '',
          street: '',
          state: '',
          zipCode: '',
        },
      },
    };
    // TODO: send data to api
    setSubmitttedValues(data);
    try {
      await updateUserAccount.mutateAsync(updateUserPayload);

      UI.notifications.success('The user has been successfully updated.');
    } catch (error) {
      UI.notifications.error(`${error as string}, Error happended during user update`);
      return;
    }
  };

  const onChange = (data: Partial<AccountFormValues>) => {
    otherProps.onChange?.(data);
  };

  return (
    <>
      <Form<Partial<AccountFormValues>>
        schema={schema}
        onSubmit={onSubmit}
        onChange={onChange}
        hasSubmitButton
        submitButtonText="Save changes"
        fieldsetLayoutClasses="grid grid-cols-1 @sm:grid-cols-2 gap-4"
        className={cn('@container', className)}
        defaultValues={defaultValues}
        append={append}
        submitButtonProps={{
          loading: updateUserAccount.isLoading,
        }}
      />
    </>
  );
};
